import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { ContentBlockType } from '@/features/editor/utils/content-blocks';

interface BlockPaletteProps {
  onAddBlock: (type: ContentBlockType) => void;
}

const blockTypes: Array<{
  type: ContentBlockType;
  label: string;
  icon: string;
  description: string;
}> = [
  {
    type: 'heading',
    label: 'Heading',
    icon: 'H',
    description: 'Add a heading (H1-H6)'
  },
  {
    type: 'paragraph',
    label: 'Paragraph',
    icon: 'P',
    description: 'Add a text paragraph'
  },
  {
    type: 'image',
    label: 'Image',
    icon: '🖼️',
    description: 'Add an image with caption'
  },
  {
    type: 'video',
    label: 'Video',
    icon: '🎥',
    description: 'Local video/GIF or default Cloudflare stream'
  },
  {
    type: 'podcast',
    label: 'Podcast',
    icon: '🎧',
    description: 'Embed a podcast episode'
  },
  {
    type: 'quote',
    label: 'Quote',
    icon: '💬',
    description: 'Add a testimonial quote'
  },
  {
    type: 'statistics',
    label: 'Statistics',
    icon: '📊',
    description: 'Add statistics with visual circles'
  },
  {
    type: 'bullet-list',
    label: 'Bullet List',
    icon: '•',
    description: 'Add a bullet point list'
  },
  {
    type: 'blog-block',
    label: 'Blog Block',
    icon: '📝',
    description: 'Add a styled content block'
  },
  {
    type: 'cta-button',
    label: 'CTA Button',
    icon: '🔗',
    description: 'Add a call-to-action button'
  },
  {
    type: 'spacer',
    label: 'Spacer',
    icon: '⬜',
    description: 'Add vertical spacing'
  }
];

export function BlockPalette({ onAddBlock }: BlockPaletteProps) {
  return (
    <div>
      <h3 className="text-lg font-semibold mb-4">Content Blocks</h3>
      <p className="text-sm text-gray-600 mb-4">
        Drag blocks to content area or click to add
      </p>
      
      <div className="space-y-2">
        {blockTypes.map((blockType) => (
          <DraggableBlockType
            key={blockType.type}
            blockType={blockType}
            onAddBlock={onAddBlock}
          />
        ))}
      </div>
    </div>
  );
}

interface DraggableBlockTypeProps {
  blockType: {
    type: ContentBlockType;
    label: string;
    icon: string;
    description: string;
  };
  onAddBlock: (type: ContentBlockType) => void;
}

function DraggableBlockType({ blockType, onAddBlock }: DraggableBlockTypeProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
  } = useDraggable({
    id: `palette-${blockType.type}`,
  });

  const style = {
    transform: CSS.Translate.toString(transform),
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`transition-all ${
        isDragging ? 'opacity-50 scale-105' : ''
      }`}
    >
      <button
        onClick={() => onAddBlock(blockType.type)}
        className="w-full text-left p-3 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors group cursor-grab active:cursor-grabbing"
        {...attributes}
        {...listeners}
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-sm font-medium group-hover:bg-blue-100">
            {blockType.icon}
          </div>
          <div>
            <div className="font-medium text-sm">{blockType.label}</div>
            <div className="text-xs text-gray-500">{blockType.description}</div>
          </div>
        </div>
      </button>
    </div>
  );
}
